import React, { useState, useEffect } from 'react';
import { sendEmailVerification } from 'firebase/auth';
import { FaEnvelope, FaEdit, FaSave } from 'react-icons/fa';
import { useFireAuth } from '../FireAuthContext';
import ProfileFunctions from './ProfileFunctions';
import Inventory from './Inventory';
import DelayRender from './DelayRender';
import MembershipTrophy from './MembershipTrophy';
import './Profile.css';
import { formatMoney } from '../utils/FormatUtils';

const Profile = () => {
  const { user, loading, updateUser, updateUserField } = useFireAuth();

  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [newBio, setNewBio] = useState('');
  const [profileMessage, setProfileMessage] = useState('');

  useEffect(() => {
    if (user) {
      setNewStatus(user.status || '');
      setNewName(user.name || '');
      setNewBio(user.bio || '');
    }
  }, [user]);

  const handleUpdateStatus = async () => {
    if (!newStatus || newStatus.length > 74) return;

    try {
      await updateUserField('status', newStatus);
      await updateUser();
      setIsEditingStatus(false);
    } catch (error) {
      console.error('Error updating status:', error);
      setProfileMessage('Failed to update status. Please try again.');
    }
  };

  const handleUpdateName = async () => {
    if (!newName) return;

    try {
      await updateUserField('name', newName);
      await updateUser();
      setIsEditingName(false);
    } catch (error) {
      console.error('Error updating name:', error);
      setProfileMessage('Failed to update name. Please try again.');
    }
  };

  const handleUpdateBio = async () => {
    if (!newBio) return;

    try {
      await updateUserField('bio', newBio);
      await updateUser();
      setIsEditingBio(false);
    } catch (error) {
      console.error('Error updating bio:', error);
      setProfileMessage('Failed to update bio. Please try again.');
    }
  };

  const handleSendVerification = async (e) => {
    e.preventDefault();
    try {
      await sendEmailVerification(user);
      setProfileMessage('Verification email sent. Please check your inbox.');
    } catch (error) {
      console.error('Error sending verification email:', error);
      setProfileMessage('Failed to send verification email. Please try again later.');
    }
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  if (!user) {
    return <p>Please log in to view your profile.</p>;
  }

  return (
    <div className="profile-section">
      <div className="profile-container">
        {profileMessage && <p className="profile-message">{profileMessage}</p>}
        <div className="profile-row">
          <div className="profile-card profile-info">
            <div className="profile-picture-container">
              <img src={user.profilePicture || 'default-avatar.png'} alt="Profile" className="profile-picture" />
              <ProfileFunctions userData={{ ...user, userId: user.uid }} setUserData={updateUser} />
            </div>
            <div className="profile-name-container">
              {isEditingName ? (
                <div className="update-name">
                  <input
                    type="text"
                    placeholder="Enter new name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                  <button className="save-icon" onClick={handleUpdateName}>
                    <FaSave />
                  </button>
                </div>
              ) : (
                <div className="name-display">
                  <h3 className="profile-user-name">{newName}</h3>
                  <button onClick={() => setIsEditingName(!isEditingName)} className="edit-name-btn">
                    <FaEdit />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="profile-card profile-stats">
            <h3>Details</h3>
            <div className="profile-details">
              <p><strong>Club #:</strong> {user.membershipNumber}</p>
              <p><strong>Member since:</strong> {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'Not available'}</p>
              <p><strong>Balance:</strong> 💴 {formatMoney(user.money)} /  🪙 {formatMoney(user.crypto?.hahumCoin || 0)}</p>
              <p><strong>Membership Status:</strong> <MembershipTrophy status={user.membershipStatus} /> {user.membershipStatus}</p>
              <p><strong>Games Played:</strong> {user.gamesPlayed || 0}</p>
              <p>
                <strong>Verified:</strong> {user.emailVerified ? '☑️' : 'No'}
                {!user.emailVerified && (
                  <a
                    href="/portal/profile"
                    onClick={handleSendVerification}
                    title="Send Verification Email"
                  >
                    <FaEnvelope className="nav-icon" />
                  </a>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="profile-card">
          <h3>Info</h3>
          <div className="profile-details">
            {isEditingBio ? (
              <div className="update-bio">
                <input
                  type="text"
                  placeholder="Enter new bio"
                  value={newBio}
                  onChange={(e) => setNewBio(e.target.value)}
                />
                <button className="save-icon" onClick={handleUpdateBio}>
                  <FaSave />
                </button>
              </div>
            ) : (
              <div className="bio-display">
                <p><strong>Bio:</strong> {newBio}</p>
                <button onClick={() => setIsEditingBio(!isEditingBio)} className="edit-bio-btn">
                  <FaEdit />
                </button>
              </div>
            )}
            <div className="profile-status-container">
              {isEditingStatus ? (
                <div className="update-status">
                  <input
                    type="text"
                    placeholder="Enter new status (max 74 chars)"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    maxLength="74"
                  />
                  <button className="save-icon" onClick={handleUpdateStatus}>
                    <FaSave />
                  </button>
                </div>
              ) : (
                <div className="status-display">
                  <p><strong>Status:</strong> {newStatus}</p>
                  <button onClick={() => setIsEditingStatus(!isEditingStatus)} className="edit-status-btn">
                    <FaEdit />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="profile-card profile-inventory">
          <Inventory user={user} updateUser={updateUser} />
        </div>
      </div>
    </div>
  );
};

export default DelayRender(Profile, 300);
